import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50 dark:bg-gray-900" }
const _hoisted_2 = { class: "p-6" }
const _hoisted_3 = { class: "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6" }
const _hoisted_4 = { class: "p-6" }
const _hoisted_5 = { class: "mb-4" }
const _hoisted_6 = { class: "relative" }
const _hoisted_7 = { class: "flex flex-wrap items-center gap-4" }
const _hoisted_8 = { class: "flex items-center space-x-2" }
const _hoisted_9 = {
  key: 0,
  class: "mt-4 flex flex-wrap gap-2"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6" }
const _hoisted_12 = { class: "p-6" }
const _hoisted_13 = { class: "flex items-center space-x-2" }
const _hoisted_14 = { class: "text-lg font-semibold text-gray-900 dark:text-white" }
const _hoisted_15 = { class: "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700" }
const _hoisted_16 = { class: "p-6" }
const _hoisted_17 = {
  key: 0,
  class: "flex items-center justify-center py-8"
}
const _hoisted_18 = { class: "flex items-center space-x-3" }
const _hoisted_19 = { class: "min-w-0 flex-1" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "flex items-start" }
const _hoisted_22 = { class: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-mono" }
const _hoisted_23 = { class: "flex flex-col gap-1.5 py-1" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = ["onClick"]
const _hoisted_26 = { class: "flex items-start" }
const _hoisted_27 = { class: "text-sm text-gray-900 dark:text-white font-medium" }
const _hoisted_28 = { class: "flex flex-wrap gap-1" }
const _hoisted_29 = { class: "flex items-center space-x-2" }
const _hoisted_30 = ["onClick"]
const _hoisted_31 = { class: "mt-6 flex items-center justify-between" }
const _hoisted_32 = { class: "text-sm text-gray-700 dark:text-gray-300" }
const _hoisted_33 = { class: "flex items-center space-x-2" }
const _hoisted_34 = ["disabled"]
const _hoisted_35 = ["disabled", "onClick"]
const _hoisted_36 = ["disabled"]

import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import DataTable from '../../components/shared/DataTable/index.vue'
import { packageVersionService } from '../../services/package-version'
import type { PackageVersion } from '../../types/package-version'
import { logger } from '../../utils/logger'

const itemsPerPage = 10

// Table Configuration

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter()

// Data
const packages = ref<PackageVersion[]>([])
const loading = ref(false)
const expandedVersions = ref<Set<string>>(new Set())

// Search and Filters
const searchQuery = ref('')
const selectedEnvironment = ref('')

// Pagination
const currentPage = ref(1)
const tableColumns = [
  { field: 'packageName', label: 'Package Name', sortable: true, searchable: true },
  { field: 'latestVersion', label: 'Latest', sortable: true },
  { field: 'versions', label: 'Versions', sortable: true },
  { field: 'workloadCount', label: 'Workloads', sortable: true },
  { field: 'environments', label: 'Env', sortable: true }
]

const tableConfig = {
  enableSearch: false, // We handle search in the parent
  enableSort: true,
  enablePagination: false, // We handle pagination in the parent
  enableFilter: false
}

// Computed Properties
const activeFilters = computed(() => {
  const filters: Record<string, string> = {}
  if (selectedEnvironment.value) filters.environment = selectedEnvironment.value
  return filters
})

const hasActiveFilters = computed(() => Object.keys(activeFilters.value).length > 0)

const filteredPackages = computed(() => {
  let result = [...packages.value]

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(
      (pkg) =>
        pkg.packageName.toLowerCase().includes(query) ||
        pkg.latestVersion.toLowerCase().includes(query) ||
        pkg.environments.some((env: string) => env.toLowerCase().includes(query))
    )
  }

  // Apply filters
  if (selectedEnvironment.value) {
    result = result.filter((pkg) => pkg.environments.includes(selectedEnvironment.value))
  }

  return result
})

const totalPages = computed(() => Math.ceil(filteredPackages.value.length / itemsPerPage))
const paginatedPackages = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredPackages.value.slice(start, end)
})

const displayedPages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const range: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) range.push(i)
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 5; i++) range.push(i)
      range.push('...', total)
    } else if (current >= total - 2) {
      range.push(1, '...')
      for (let i = total - 4; i <= total; i++) range.push(i)
    } else {
      range.push(1, '...')
      for (let i = current - 1; i <= current + 1; i++) range.push(i)
      range.push('...', total)
    }
  }

  return range
})

// Methods
const loadPackages = async () => {
  try {
    loading.value = true
    const response = await packageVersionService.getPackages()
    logger.log('API Response:', response)
    // The response should match PackageVersionsResponse type
    if (response && Array.isArray(response.data)) {
      packages.value = response.data
      logger.log('Parsed packages:', packages.value)
    } else {
      logger.error('Invalid response format:', response)
      packages.value = []
    }
  } catch (error) {
    logger.error('Failed to load packages:', error)
  } finally {
    loading.value = false
  }
}

const clearAllFilters = () => {
  searchQuery.value = ''
  selectedEnvironment.value = ''
  currentPage.value = 1
}

const removeFilter = (key: string) => {
  if (key === 'environment') {
    selectedEnvironment.value = ''
  }
  currentPage.value = 1
}

const getFilterLabel = (key: string) => {
  const labels: Record<string, string> = {
    environment: 'Environment',
    status: 'Status',
    version: 'Version'
  }
  return labels[key] || key
}

const handleRowClick = (item: PackageVersion) => {
  // Navigate to package details or handle row click
  logger.log('Row clicked:', item)
}

const viewPackageDetails = (item: PackageVersion) => {
  // Navigate to package details page
  logger.log('View details for:', item)
}

const toggleVersions = (packageName: string) => {
  if (expandedVersions.value.has(packageName)) {
    expandedVersions.value.delete(packageName)
  } else {
    expandedVersions.value.add(packageName)
  }
  // Trigger reactivity
  expandedVersions.value = new Set(expandedVersions.value)
}

const navigateToPackageDetails = (packageName: string, version?: string) => {
  if (version) {
    router.push(`/packages/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}`)
  } else {
    router.push(`/packages/${encodeURIComponent(packageName)}`)
  }
}

// Lifecycle
onMounted(() => {
  loadPackages()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[11] || (_cache[11] = _createStaticVNode("<div class=\"bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700\" data-v-412ee2e6><div class=\"px-6 py-4\" data-v-412ee2e6><div class=\"flex items-center justify-between\" data-v-412ee2e6><div class=\"flex items-center space-x-4\" data-v-412ee2e6><div class=\"flex items-center space-x-2\" data-v-412ee2e6><div class=\"w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center\" data-v-412ee2e6><svg class=\"w-5 h-5 text-blue-600 dark:text-blue-300\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-412ee2e6><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4\" data-v-412ee2e6></path></svg></div><h1 class=\"text-2xl font-bold text-gray-900 dark:text-white\" data-v-412ee2e6>Package View</h1></div></div><div class=\"flex items-center space-x-3\" data-v-412ee2e6><button class=\"flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors\" data-v-412ee2e6><svg class=\"w-4 h-4\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-412ee2e6><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z\" data-v-412ee2e6></path></svg><span data-v-412ee2e6>Export</span></button><button class=\"flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors\" data-v-412ee2e6><svg class=\"w-4 h-4\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-412ee2e6><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M12 6v6m0 0v6m0-6h6m-6 0H6\" data-v-412ee2e6></path></svg><span data-v-412ee2e6>Add</span></button></div></div></div></div>", 1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                _createElementVNode("svg", {
                  class: "h-5 w-5 text-gray-400",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  })
                ])
              ], -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                type: "text",
                class: "w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                placeholder: "Search packages: @platform-core/base-service..."
              }, null, 512), [
                [_vModelText, searchQuery.value]
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[6] || (_cache[6] = _createElementVNode("label", { class: "text-sm font-medium text-gray-700 dark:text-gray-300" }, "Environment:", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedEnvironment).value = $event)),
                class: "px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              }, _cache[5] || (_cache[5] = [
                _createElementVNode("option", { value: "" }, "All", -1),
                _createElementVNode("option", { value: "interop" }, "Interop", -1),
                _createElementVNode("option", { value: "staging" }, "Staging", -1),
                _createElementVNode("option", { value: "production" }, "Production", -1)
              ]), 512), [
                [_vModelSelect, selectedEnvironment.value]
              ])
            ]),
            _createElementVNode("button", {
              class: "px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors",
              onClick: clearAllFilters
            }, " Clear All ")
          ]),
          (hasActiveFilters.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (value, key) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: key,
                    class: "inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                  }, [
                    _createTextVNode(_toDisplayString(getFilterLabel(key)) + ": " + _toDisplayString(value) + " ", 1),
                    _createElementVNode("button", {
                      class: "ml-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100",
                      onClick: ($event: any) => (removeFilter(key))
                    }, " × ", 8, _hoisted_10)
                  ]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center" }, [
              _createElementVNode("svg", {
                class: "w-5 h-5 text-green-600 dark:text-green-300",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                })
              ])
            ], -1)),
            _createElementVNode("h2", _hoisted_14, " Summary: " + _toDisplayString(filteredPackages.value.length) + " packages found ", 1)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "text-lg font-semibold text-gray-900 dark:text-white mb-4" }, "Packages Table", -1)),
          (loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, _cache[8] || (_cache[8] = [
                _createElementVNode("div", { class: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" }, null, -1),
                _createElementVNode("span", { class: "ml-3 text-gray-600 dark:text-gray-400" }, "Loading packages...", -1)
              ])))
            : (_openBlock(), _createBlock(DataTable, {
                key: 1,
                data: paginatedPackages.value,
                columns: tableColumns,
                config: tableConfig,
                onRowClick: handleRowClick
              }, {
                packageName: _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_18, [
                    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center" }, [
                      _createElementVNode("svg", {
                        class: "w-4 h-4 text-blue-600 dark:text-blue-300",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        })
                      ])
                    ], -1)),
                    _createElementVNode("div", _hoisted_19, [
                      _createElementVNode("button", {
                        class: "font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-left",
                        onClick: _withModifiers(($event: any) => (navigateToPackageDetails(item.packageName)), ["stop"])
                      }, _toDisplayString(item.packageName), 9, _hoisted_20)
                    ])
                  ])
                ]),
                latestVersion: _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_21, [
                    _createElementVNode("span", _hoisted_22, _toDisplayString(item.latestVersion), 1)
                  ])
                ]),
                versions: _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_23, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(expandedVersions.value.has(item.packageName)
                    ? item.versions
                    : item.versions.slice(0, 3), (version) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: version,
                        class: "inline-flex items-center text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded w-fit hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                        onClick: _withModifiers(($event: any) => (navigateToPackageDetails(item.packageName, version)), ["stop"])
                      }, _toDisplayString(version), 9, _hoisted_24))
                    }), 128)),
                    (item.versions.length > 3)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: "text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-left font-medium w-fit",
                          onClick: _withModifiers(($event: any) => (toggleVersions(item.packageName)), ["stop"])
                        }, _toDisplayString(expandedVersions.value.has(item.packageName)
                      ? 'Show less'
                      : `+${item.versions.length - 3} more`), 9, _hoisted_25))
                      : _createCommentVNode("", true)
                  ])
                ]),
                workloadCount: _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_26, [
                    _createElementVNode("span", _hoisted_27, _toDisplayString(item.workloadCount), 1)
                  ])
                ]),
                environments: _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_28, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.environments, (env) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: env,
                        class: _normalizeClass([
                    'inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium',
                    env === 'interop'
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                      : env === 'staging'
                        ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  ])
                      }, _toDisplayString(env), 3))
                    }), 128))
                  ])
                ]),
                "row-actions": _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_29, [
                    _createElementVNode("button", {
                      class: "text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium",
                      onClick: _withModifiers(($event: any) => (viewPackageDetails(item)), ["stop"])
                    }, " View Details ", 8, _hoisted_30)
                  ])
                ]),
                _: 1
              }, 8, ["data"]))
        ])
      ]),
      _createElementVNode("div", _hoisted_31, [
        _createElementVNode("div", _hoisted_32, " Page " + _toDisplayString(currentPage.value) + " of " + _toDisplayString(totalPages.value), 1),
        _createElementVNode("div", _hoisted_33, [
          _createElementVNode("button", {
            disabled: currentPage.value === 1,
            class: "px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (currentPage.value--))
          }, " ← Previous ", 8, _hoisted_34),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedPages.value, (page) => {
            return (_openBlock(), _createElementBlock("button", {
              key: page,
              disabled: page === '...',
              class: _normalizeClass([
              'px-3 py-2 text-sm font-medium rounded-md',
              currentPage.value === page
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            ]),
              onClick: ($event: any) => (currentPage.value = Number(page))
            }, _toDisplayString(page), 11, _hoisted_35))
          }), 128)),
          _createElementVNode("button", {
            disabled: currentPage.value === totalPages.value,
            class: "px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (currentPage.value++))
          }, " Next → ", 8, _hoisted_36)
        ])
      ])
    ])
  ]))
}
}

})